import {
    Component,
    OnInit,
    OnChanges,
    Input,
    Output,
    EventEmitter,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { RtTripUK, RtClmView, RtvTripError, RtvTripErrorAdapter } from '@bds/railtrac-models';
import { PopoutEvent } from '../../popout-event';
import {
    faSpinner,
    faCheck,
    faExclamation,
    faClipboard,
    faPlus,
    faArrowRotateRight,
    faFileImport,
} from '@fortawesome/pro-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons';
import { faTrain, faClmHistory, faSave } from '@bds/fa-svg-icons';
import { RtDiversionService } from '../../rt-diversion';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScrollSpyService } from '@bds/helpers';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { EquipReportCategoryService, EquipReportCatgValueService } from '@bds/equipment';
import { EquipReportCategory, EquipReportCatgValue } from '@bds/equipment';
import {
    BdsCustomerService,
    CommodityShippedService,
    RtCustomerTypeDefaultService,
    ClmSightCodeService,
} from '@bds/data-access';
import { ErrorListItem } from '@bds/rt-advance-models';
import { RtCommodityShippedForm } from '../../railtrac-trip/rt-commodity-shipped-form.service';
import { RtTripErrorService } from '@bds/railtrac';
import { CommunicationMessage, CommunicationService } from '@bds/core';
import { Router } from '@angular/router';

interface ISelectedTrip {
    id: number;
    tripError?: RtvTripError;
    key: RtTripUK;
    state: 'none' | 'saving' | 'saved' | 'failed';
    errors?: ErrorListItem[];
}

@Component({
    selector: 'rt-trip-error-multi',
    templateUrl: './rt-trip-error-multi.component.html',
    styleUrls: ['./rt-trip-error-multi.component.scss'],
    providers: [RtCommodityShippedForm],
})
export class RtTripErrorMultiComponent implements OnInit, OnChanges {
    @Input() ormIds: number[];
    @Input() allowPopout: boolean;
    @Input() isReadOnly = false;
    @Output() popout: EventEmitter<PopoutEvent<number>> = new EventEmitter<PopoutEvent<number>>();

    currentHttpRequest: Subscription;
    selectedTripKeys: RtTripUK[];
    selectedTrips: ISelectedTrip[];
    selectedTripError: RtvTripError;
    tripErrors: RtvTripError[];

    currentClm: RtClmView;
    sightCodes: Observable<any[]>;
    disabledClmFields: string[] = [];
    routeReadOnly = true;

    equipCategoriesGroup: { [key: number]: EquipReportCategory };
    equipCatgValuesGroup: { [key: number]: EquipReportCatgValue };

    isDirty = false;
    isSaving = false;
    loading = false;
    isChangesNotSaved = false;

    private readonly saveConcurrentCalls = 5;
    // The reprocess stored procedure handles a list of ids in one call, so send batches one at a time
    private readonly reprocessBatchSize = 25;
    private readonly reprocessRetryDelayMs = 1500;
    private currentConcurrency = 0;
    progressDone = 0;
    progressTotal = 0;

    iconSpinner = faSpinner;
    iconTrain = faTrain;
    iconAdd = faPlus;
    reprocess = faArrowRotateRight;
    iconDelete = faTrashAlt;
    iconStatusSaved = faCheck;
    iconStatusSaving = faSpinner;
    iconStatusFailed = faExclamation;
    iconStatusNone = faClipboard;
    history = faClmHistory;
    save = faSave;
    import = faFileImport;

    @ViewChild('tripdiversionpanel') tripdiversionpanel: MatExpansionPanel;
    @ViewChild('tripErrorDetails') tripErrorDetailsComponent: any;

    constructor(
        public tripErrorService: RtTripErrorService,
        private equipReportCategoryService: EquipReportCategoryService,
        private equipReportCatgValueService: EquipReportCatgValueService,
        private _snackbar: MatSnackBar,
        public scrollSpyService: ScrollSpyService,
        public dialog: MatDialog,
        public tripDiversionService: RtDiversionService,
        private commodityService: CommodityShippedService,
        private customerApiService: BdsCustomerService,
        public communicationService: CommunicationService,
        public customerTypeDefaultService: RtCustomerTypeDefaultService,
        private clmSightCodeService: ClmSightCodeService,
        private router: Router,
        private adapter: RtvTripErrorAdapter,
    ) {}

    ngOnInit() {
        this.getCategories();
        this.getCategoryValues();
        this.sightCodes = this.clmSightCodeService.getAll();
    }

    //#region EquipmentCategory
    getCategories(): void {
        this.equipReportCategoryService
            .getAll()
            .pipe(take(1))
            .subscribe((res) => {
                const grp: { [key: number]: EquipReportCategory } = {};
                res.map((m) => {
                    grp[m.ormId] = m;
                });
                this.equipCategoriesGroup = grp;
            });
    }

    onFormDirtyChange($event): void {
        this.isChangesNotSaved = $event;
    }

    async reProcess(): Promise<void> {
        if (!this.ormIds || this.ormIds.length === 0) {
            return;
        }

        this.isSaving = true;

        const failedTrips: RtvTripError[] = [];
        const failedIds = new Set<number>();

        // Returns false when the request itself failed, so the caller can retry
        const reprocessIds = async (ids: number[]): Promise<boolean> => {
            this.setTripState(ids, 'saving');
            try {
                const result = await firstValueFrom(this.tripErrorService.reProcess(ids));
                // Records still in error come back under the ids that were sent
                const stillInError = result?.relatedObject ?? [];
                const stillInErrorIds = new Set(stillInError.map((te) => te.ormId));
                failedTrips.push(...stillInError);

                ids.forEach((id) => {
                    const failed = stillInErrorIds.has(id);
                    if (failed) {
                        failedIds.add(id);
                    }
                    this.setTripState([id], failed ? 'failed' : 'saved');
                });
                return true;
            } catch (err) {
                console.error(`Error reprocessing trip(s) ${ids.join(', ')}:`, err);
                this.setTripState(ids, 'failed');
                return false;
            }
        };

        const reprocessWithRetry = async (id: number): Promise<void> => {
            if (await reprocessIds([id])) {
                return;
            }
            // One retry for transient failures such as a deadlock victim
            await new Promise((resolve) => setTimeout(resolve, this.reprocessRetryDelayMs));
            if (!(await reprocessIds([id]))) {
                failedIds.add(id);
            }
        };

        try {
            this.progressDone = 0;
            this.progressTotal = this.ormIds.length;

            for (let i = 0; i < this.ormIds.length; i += this.reprocessBatchSize) {
                const batch = this.ormIds.slice(i, i + this.reprocessBatchSize);

                // The stored procedure runs a batch in one transaction, so a failed batch is retried one id at a time
                if (batch.length === 1 || !(await reprocessIds(batch))) {
                    for (const id of batch) {
                        await reprocessWithRetry(id);
                    }
                }
                this.progressDone += batch.length;
            }
        } finally {
            this.isSaving = false;
            this.progressTotal = 0;
        }

        const message: CommunicationMessage = {
            subscriberName: 'TripErrorScreen',
            action: 'reprocess',
            data: failedTrips,
        };
        this.communicationService.sendData(message);

        if (failedIds.size === 0) {
            this._snackbar.open('Trip(s) reprocessed successfully.', 'Ok', { duration: 3000 });
            void this.router.navigate(['interface-validation'], {
                queryParamsHandling: 'preserve',
            });
            return;
        }

        this._snackbar.open(`Reprocess failed for ${failedIds.size} trip(s).`, 'Info', {
            duration: 3000,
        });
    }

    onDeleteProductClick(ormId: number): void {
        this.commodityService
            .delete(ormId)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this._snackbar.open('Product deleted.', 'Ok');
                    // this.selectedCommodityShippedKeys = this.selectedCommodityShippedKeys.filter(
                    //     (data) => data.ormId !== ormId,
                    // );
                },
                error: () => {
                    this._snackbar.open('Error during delete.', 'Ok');
                },
            });
    }

    getCategoryValues(): void {
        this.equipReportCatgValueService
            .getAll()
            .pipe(take(1))
            .subscribe((res) => {
                const grp: { [key: number]: EquipReportCatgValue } = {};
                res.map((m) => {
                    grp[m.ormId] = m;
                });
                this.equipCatgValuesGroup = grp;
            });
    }

    async fetchData(): Promise<void> {
        if (!this.ormIds || this.ormIds.length === 0) {
            return;
        }
        // this.loading = true;

        const [combined, tripErrors] = await Promise.all([
            firstValueFrom(this.tripErrorService.readCombined(this.ormIds)),
            firstValueFrom(this.tripErrorService.findTripErrors(this.ormIds)),
        ]);
        this.selectedTripError = combined.trip;
        this.tripErrors = tripErrors;
        this.syncSelectedTrips();
    }

    // Rebuild the side list from the grid selection, keeping state for trips still selected
    private syncSelectedTrips(): void {
        const previous = new Map((this.selectedTrips ?? []).map((t) => [t.id, t]));
        this.selectedTrips = (this.ormIds ?? []).map((id) => {
            const tripError = this.tripErrors?.find((te) => te.ormId === id);
            return {
                id,
                tripError,
                key: { ormId: id, carInit: tripError?.carInit, carNo: tripError?.carNo },
                state: previous.get(id)?.state ?? 'none',
                errors: tripError?.errorDescription
                    ? [{ field: '', errorMessage: tripError.errorDescription }]
                    : [],
            };
        });
    }

    private setTripState(ids: number[], state: ISelectedTrip['state']): void {
        this.selectedTrips?.forEach((trip) => {
            if (ids.includes(trip.id)) {
                trip.state = state;
            }
        });
    }

    // Workers on a shared queue cap concurrent API/DB load and drive progress. Workers above the
    // current limit idle, so the limit can be lowered and raised while the pool runs.
    private async runPool<T>(
        items: T[],
        worker: (item: T) => Promise<void>,
        maxConcurrency: number,
        startConcurrency: number = maxConcurrency,
    ): Promise<void> {
        const queue = [...items];
        this.progressDone = 0;
        this.progressTotal = queue.length;
        this.currentConcurrency = startConcurrency;

        const run = async (index: number): Promise<void> => {
            while (queue.length > 0) {
                if (index >= this.currentConcurrency) {
                    await new Promise((resolve) => setTimeout(resolve, 200));
                    continue;
                }
                await worker(queue.shift() as T);
                this.progressDone++;
            }
        };

        try {
            const workerCount = Math.min(maxConcurrency, queue.length);
            await Promise.all(Array.from({ length: workerCount }, (_, i) => run(i)));
        } finally {
            this.progressTotal = 0;
        }
    }

    private carIdSort = (
        a: { carInit: string; carNo: string },
        b: { carInit: string; carNo: string },
    ) => {
        const nameA = `${(a.carInit + a.carNo).toUpperCase()}`;
        const nameB = `${(b.carInit + b.carNo).toUpperCase()}`;
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        // names must be equal
        return 0;
    };

    ngOnChanges(changes: SimpleChanges) {
        const tripIdChange = changes['ormIds'];

        if (!!tripIdChange) {
            const tripIdCurrent: number[] = tripIdChange.currentValue;
            this.fetchData();
        }
    }

    getTripInitiateStatus(custNo: string, le: string): Observable<string> {
        return this.customerApiService.getByCustNo(custNo).pipe(
            switchMap((customer) => {
                return this.customerTypeDefaultService
                    .getByCustTypeAndLE(customer?.customerType, le)
                    .pipe(
                        map((customerType) => {
                            return customerType ? customerType.beginStatus : '';
                        }),
                    );
            }),
        );
    }

    /**
     * set selected section
     * @param section string
     */
    setCurrentSection(section: string): void {
        this.scrollSpyService.changeSection(section);
    }

    getTripErrorTooltip(trip: ISelectedTrip): string {
        return (trip.errors ?? [])
            .map((error) => error.errorMessage)
            .filter((message) => !!message)
            .join('\n');
    }

    showTrip(ormId: number): void {
        // Update selectedTripError from tripErrors list based on ormId
        const tripError = this.tripErrors?.find((te) => te.ormId === ormId);
        if (tripError) {
            this.selectedTripError = tripError;

            // Force change detection to ensure the details component is properly updated
            setTimeout(() => {
                // This ensures the rt-trip-error-details component has time to reinitialize
                // with the new selectedTripError data
            }, 0);
        }
    }

    reloadClms(): void {
        // Reload CLM data for the current selected trip
        if (this.selectedTripError?.ormId) {
        }
    }

    async processTrips(useAddImport: boolean = false): Promise<void> {
        if (!this.ormIds || this.ormIds.length === 0) {
            this._snackbar.open('No trips selected for import', 'Warning', { duration: 3000 });
            return;
        }

        this.isSaving = true;

        try {
            // Get only the changed form values
            const changedFormValues = this.getChangedFormValues();

            if (
                (!changedFormValues || Object.keys(changedFormValues).length === 0) &&
                !useAddImport
            ) {
                this._snackbar.open('No form changes detected to update', 'Info', {
                    duration: 3000,
                });
                this.isSaving = false;
                return;
            }

            // Create array to store updated trip errors
            const succeeded: RtvTripError[] = [];
            let failedCount = 0;

            // Process each ormId (exclude the currently selected one)
            const ormIdsToUpdate = this.ormIds.filter((id) => id !== this.selectedTripError?.ormId);

            await this.runPool(
                ormIdsToUpdate,
                async (ormId) => {
                    this.setTripState([ormId], 'saving');
                    try {
                        // Get the individual trip error
                        const result = await firstValueFrom(this.tripErrorService.read(ormId));
                        const tripError = this.adapter.adapt(result);

                        if (!tripError) {
                            throw new Error(`Trip error ${ormId} not found`);
                        }

                        const updatedTripError: RtvTripError = {
                            ...tripError, // Keep all original values
                            ...changedFormValues, // Only override with changed values
                        } as RtvTripError;

                        await firstValueFrom(
                            useAddImport
                                ? this.tripErrorService.addImport(updatedTripError)
                                : this.tripErrorService.updateInterfaceValidation(updatedTripError),
                        );

                        succeeded.push(updatedTripError);
                        this.setTripState([ormId], 'saved');
                    } catch (error) {
                        console.error(`Error processing trip error for ormId ${ormId}:`, error);
                        this.setTripState([ormId], 'failed');
                        failedCount++;
                    }
                },
                this.saveConcurrentCalls,
            );

            if (succeeded.length > 0 || failedCount > 0) {
                if (succeeded.length > 0) {
                    this._snackbar.open(
                        `Successfully ${useAddImport ? 'imported' : 'updated'} data to ${
                            succeeded.length
                        } trip(s)` + (failedCount > 0 ? `. ${failedCount} trip(s) failed.` : ''),
                        failedCount > 0 ? 'Info' : 'Success',
                        { duration: 5000 },
                    );

                    const message: CommunicationMessage = {
                        subscriberName: 'TripErrorScreen',
                        action: 'refresh',
                        data: succeeded,
                    };
                    this.communicationService.sendData(message);
                    // Refresh the data to show updates
                    await this.fetchData();
                } else {
                    this._snackbar.open(
                        `Error ${useAddImport ? 'importing' : 'updating'} data to trips`,
                        'Error',
                        { duration: 3000 },
                    );
                }
            } else {
                this._snackbar.open('No trips were updated', 'Info', { duration: 3000 });
            }
        } catch (error) {
            this.selectedTrips?.forEach((trip) => {
                if (trip.state === 'saving') {
                    trip.state = 'failed';
                }
            });
            console.error(`Error in ${useAddImport ? 'addImports' : 'update'}:`, error);
            this._snackbar.open(
                `Error ${useAddImport ? 'importing' : 'updating'} data to trips`,
                'Error',
                { duration: 3000 },
            );
        } finally {
            this.isSaving = false;
        }
    }

    // Simplified public methods
    async addImports(): Promise<void> {
        await this.processTrips(true);
    }

    async update(): Promise<void> {
        await this.processTrips(false);
        this.isChangesNotSaved = false;
    }

    private getChangedFormValues(): Partial<RtvTripError> {
        if (!this.tripErrorDetailsComponent?.tripForm) {
            console.warn('Trip form not available');
            return {};
        }

        const form = this.tripErrorDetailsComponent.tripForm;
        const changedValues: any = {};

        const importableFields = [
            'fleetId',
            'businessGroup',
            'custNo',
            'routeCode',
            'shipDateTime',
            'shipmentType',
            'origCity',
            'origState',
            'destCity',
            'destState',
            'returnCity',
            'returnState',
            'supplierFact',
            'freeRunner',
            'shopMove',
            'salesRep',
            'oneWayRound',
            'badOrderDays',
            'divertYorn',
            'freightPmtCode',
            'unitTrainId',
            'tripRef1',
            'tripRef2',
            'tripRef3',
            'tripRef4',
            'csrNo',
            'tripNo',
            'bolNo',
            'shipperNo',
            'custPoNo',
            'tripStatus',
            'carStatus',
            'freeRunner',
            'supplierShipment',
            'supplierCode',
        ];

        importableFields.forEach((fieldName) => {
            const control = form.get(fieldName);

            if (control && control.dirty) {
                // Only include if the value has actually changed and is not empty/null
                const value = control.value;

                if (value !== null && value !== undefined && value !== '') {
                    changedValues[fieldName] = value;
                }
            }
        });

        return changedValues as Partial<RtvTripError>;
    }
}
